"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { usePathname } from "@/i18n/navigation";

import { NAV_DESTINATIONS, type NavDestinationId } from "./destinations";

interface NavContextValue {
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  activeTab: NavDestinationId | null;
}

const NavContext = createContext<NavContextValue | null>(null);

const RECIPE_SUB_PAGE = /^\/recipes\/.+/;

export function NavProvider({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState<NavDestinationId | null>(null);

  useEffect(() => {
    if (RECIPE_SUB_PAGE.test(pathname)) return;
    const match = NAV_DESTINATIONS.find(
      (d) => pathname === d.href || pathname.startsWith(d.href + "/")
    );
    if (match) setActiveTab(match.id);
  }, [pathname]);

  const value = useMemo(
    () => ({ drawerOpen, openDrawer, closeDrawer, activeTab }),
    [drawerOpen, openDrawer, closeDrawer, activeTab]
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav() {
  const ctx = useContext(NavContext);
  if (!ctx) {
    throw new Error("useNav must be used within a NavProvider");
  }
  return ctx;
}
